import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../../css/Pages.css';
import ContactInformationListing from '../../components/Profile/ContactInformation/ContactInformationListing';
import ContactInformationForm from '../../components/Profile/ContactInformation/ContactInformationForm';

const ContactInformation = () => {
    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    const defaultProfile = useMemo(() => ({
        Id: 0,
        UserId: currentUser.Id || 0,
        LastName: currentUser.LastName || '',
        FirstName: currentUser.FirstName || '',
        DisplayName: '',
        Gender: 'Nam',
        DateOfBirth: null,
        PlaceOfBirth: '',
        Ethnicity: '',
        Hometown: '',
        AssignedWork: '',
        DepartmentName: '',
        DepartmentId: currentUser.DepartmentId || null,
        AdminPosition: '',
        AdminPositionStartDate: null,
        PartyPosition: '',
        ContactAddress: '',
        Email: currentUser.Email || '',
        PhoneNumber: '',
        GoogleScholarLink: '',
        OrcidLink: '',
        AvatarUrl: '',
        Degree: '',
        AcademicTitle: '',
        TeachingMajor: '',
        ResearchField: '',
        ForeignLanguage: ''
    }), [currentUser]);

    const [profile, setProfile] = useState(defaultProfile);
    const [departments, setDepartments] = useState([]);

    const [workHistories, setWorkHistories] = useState([]);
    const [educationHistories, setEducationHistories] = useState([]);
    const [researchFields, setResearchFields] = useState([]);
    const [teachingMajors, setTeachingMajors] = useState([]);
    const [academicTitles, setAcademicTitles] = useState([]);
    const [foreignLanguages, setForeignLanguages] = useState([]);
    const [awards, setAwards] = useState([]);
    const [mentorings, setMentorings] = useState([]);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [warningMessage, setWarningMessage] = useState('');
    const [isForceUpdate, setIsForceUpdate] = useState(false);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        alert("Phiên đăng nhập đã hết hạn hoặc bạn không có quyền truy cập. Vui lòng đăng nhập lại!");
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
    }, []);

    const syncWithLocalStorage = useCallback((data) => {
        const userLocal = JSON.parse(localStorage.getItem('user')) || {};
        const updatedUser = {
            ...userLocal,
            FirstName: data.FirstName || userLocal.FirstName,
            LastName: data.LastName || userLocal.LastName,
            FullName: `${data.LastName || userLocal.LastName} ${data.FirstName || userLocal.FirstName}`.trim(),
            AvatarUrl: data.AvatarUrl || userLocal.AvatarUrl,
            DepartmentId: data.DepartmentId || userLocal.DepartmentId
        };

        if (updatedUser.AvatarUrl && updatedUser.AvatarUrl.includes('base64,')) {
            delete updatedUser.AvatarUrl;
        }

        localStorage.setItem('user', JSON.stringify(updatedUser));
        window.dispatchEvent(new Event('storage'));
    }, []);

    const fetchProfile = useCallback(async (params = '') => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/contactinformation${params}`, {
                headers: getAuthHeaders()
            });

            if (res.status === 401 || res.status === 403) {
                handleLogout();
                return null;
            }

            if (res.ok) {
                const data = await res.json();
                return data;
            }
        } catch (error) {
            console.error("Lỗi fetch profile:", error);
        }
        return null;
    }, [getAuthHeaders, handleLogout]);

    const fetchData = useCallback(async (endpoint, params = '') => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}${params}`, {
                headers: getAuthHeaders()
            });

            if (res.status === 401 || res.status === 403) {
                handleLogout();
                return null;
            }

            if (res.ok) {
                return await res.json();
            }
        } catch (error) {
            console.error(`Lỗi fetch ${endpoint}:`, error);
        }
        return null;
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        let isMounted = true;

        const loadAllData = async () => {
            setIsLoading(true);
            try {
                const userLocal = JSON.parse(localStorage.getItem('user'));
                const userIdParam = userLocal?.Id ? `?userId=${userLocal.Id}` : '';

                const [
                    profileData,
                    deptData,
                    workData,
                    eduData,
                    researchData,
                    teachingData,
                    titleData,
                    langData,
                    awardData,
                    mentorData
                ] = await Promise.all([
                    fetchProfile(userIdParam),
                    fetchData('/department?mode=selection'),
                    fetchData('/workhistory', userIdParam),
                    fetchData('/educationhistory', userIdParam),
                    fetchData('/profileresearchfield', userIdParam),
                    fetchData('/profileteachingmajor', userIdParam),
                    fetchData('/profileacademictitle', userIdParam),
                    fetchData('/profileforeignlanguage', userIdParam),
                    fetchData('/profileaward', userIdParam),
                    fetchData('/profilementoring', userIdParam)
                ]);

                if (!isMounted) return;

                if (deptData) setDepartments(deptData);
                if (workData) setWorkHistories(workData);
                if (eduData) setEducationHistories(eduData);
                if (researchData) setResearchFields(researchData);
                if (teachingData) setTeachingMajors(teachingData);
                if (titleData) setAcademicTitles(titleData);
                if (langData) setForeignLanguages(langData);
                if (awardData) setAwards(awardData);
                if (mentorData) setMentorings(mentorData);

                if (profileData && profileData.UserId === userLocal?.Id) {
                    setProfile(profileData);
                    syncWithLocalStorage(profileData);

                    setWarningMessage('');
                    setIsForceUpdate(false);
                } else {
                    setProfile({
                        ...defaultProfile,
                        LastName: userLocal?.LastName || '',
                        FirstName: userLocal?.FirstName || '',
                        Email: userLocal?.Email || '',
                        UserId: userLocal?.Id || 0,
                        DepartmentId: userLocal?.DepartmentId || null
                    });

                    setWarningMessage('Chào mừng bạn đăng nhập lần đầu! Vui lòng cập nhật đầy đủ thông tin cá nhân');
                    setIsForceUpdate(true);
                    setIsEditModalOpen(true);
                }

            } catch (error) {
                console.error("Lỗi tải dữ liệu tổng hợp:", error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadAllData();

        return () => {
            isMounted = false;
        };
    }, [fetchProfile, fetchData, syncWithLocalStorage, defaultProfile]);

    const handleSave = async (updatedData) => {
        try {
            const userLocal = JSON.parse(localStorage.getItem('user'));

            const payload = {
                ...updatedData,
                UserId: userLocal.Id,
                DepartmentId: updatedData.DepartmentId ? parseInt(updatedData.DepartmentId) : null
            };

            console.log("Dữ liệu Payload chuẩn bị gửi:", JSON.stringify(payload, null, 2));

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/contactinformation`, {
                method: updatedData.Id ? 'PUT' : 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (res.status === 401 || res.status === 403) {
                handleLogout();
                return;
            }

            if (res.ok) {
                const freshProfile = await fetchProfile(`?userId=${userLocal.Id}`);
                if (freshProfile) {
                    setProfile(freshProfile);
                    syncWithLocalStorage(freshProfile);

                    setWarningMessage('');
                    setIsForceUpdate(false);
                }
                setIsEditModalOpen(false);
            } else {
                alert("Lưu thông tin thất bại!");
            }
        } catch (error) {
            console.error("Lỗi lưu thông tin:", error);
        }
    };

    return (
        <div className="page-container" style={{ backgroundColor: '#f4f6f9', minHeight: '100vh' }}>
            <div className="page-header mb-4">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>THÔNG TIN LIÊN HỆ</h2>
                </div>
            </div>

            <ContactInformationListing
                profile={profile}
                workHistories={workHistories}
                educationHistories={educationHistories}
                researchFields={researchFields}
                teachingMajors={teachingMajors}
                academicTitles={academicTitles}
                foreignLanguages={foreignLanguages}
                awards={awards}
                mentorings={mentorings}
                departments={departments}
                onEditClick={() => setIsEditModalOpen(true)}
                isLoading={isLoading}
            />

            <ContactInformationForm
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSubmit={handleSave}
                formData={profile}
                warningMessage={warningMessage}
                isForceUpdate={isForceUpdate}
                departments={departments}
            />
        </div>
    );
};

export default ContactInformation;